import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import React, { useRef, useEffect, useState } from "react";
import { useNotify } from "react-admin";
import MapGL, { MapRef } from "react-map-gl";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import DrawRectangle from "mapbox-gl-draw-rectangle-mode";
import { Box, Button } from "@mui/material";
import { area, Feature, kinks, Polygon } from "@turf/turf";
import center from "@turf/center";
import bbox from "@turf/bbox";
import bboxPolygon from "@turf/bbox-polygon";

import { MapSearchInputNoCountryCheck } from "../map/MapSearchInputNoCountryCheck";
import { getCountryCodeAlpha3 } from "../map/countryCodeAlpha3";
import { MapStylesMenu } from "../map/MapStylesMenu";
import { defaultRiskAssessmentMapStyle } from "../map/Constants";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const maxPolygonAreaAccepted = 1000; // in square km

type ReqParametersLayerProps = {
  setReqBtnDisabledFromMap: (disabled: boolean) => void;
  setPolygonCountry: (country: string | null) => void;
  newEventReqPolygon: Partial<EventScopeRequest> ;
  setNewEventReqPolygon: React.Dispatch<React.SetStateAction<Partial<EventScopeRequest>>>;
};

export const EventScopeMap: React.FC<ReqParametersLayerProps> = ({
  setReqBtnDisabledFromMap,
  setPolygonCountry,
  newEventReqPolygon,
  setNewEventReqPolygon,
}) => {
  const mapRef = useRef<MapRef | null>(null);
  const drawRef = useRef<MapboxDraw | null>(null);
  const notify = useNotify();

  const [viewState, setViewState] = useState<PartialViewState>({
    latitude: 50.300804,
    longitude: 13.866934,
    zoom: 7,
  });
  const [polygonNotOK, setPolygonNotOK] = useState<boolean>(false);
  const [mapStyle, setMapStyle] = useState(defaultRiskAssessmentMapStyle);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    if (!drawRef.current) {
      drawRef.current = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          trash: true,
        },
        modes: {
          ...MapboxDraw.modes,
          draw_rectangle: DrawRectangle,
        },
      });

      map.addControl(drawRef.current, "top-right");

      map.on("draw.create", (e: any) => {
        setNewEventReqPolygon((prev: Partial<EventScopeRequest>) => ({
          ...prev,
          aoiPolygonWKT: getPolygonInputText(e.features[0].geometry.coordinates[0]),
        }));
      });

      map.on("draw.update", (e: any) => {
        if (e.features[0].geometry.coordinates.length > 0) {
          setNewEventReqPolygon((prev: Partial<EventScopeRequest> ) => ({
            ...prev,
            aoiPolygonWKT: getPolygonInputText(e.features[0].geometry.coordinates[0]),
          }));
        } else {
          // polygon has been deleted
          setNewEventReqPolygon((prev: Partial<EventScopeRequest> ) => ({
            ...prev,
            aoiPolygonWKT: "",
          }));
        }
      });

      map.on("draw.delete", (e: any) => {
        setNewEventReqPolygon((prev: Partial<EventScopeRequest> ) => ({
          ...prev,
          aoiPolygonWKT: "",
        }));
      });
    }

    return () => {
      if (map && drawRef.current) {
        try {
          map.removeControl(drawRef.current);
          drawRef.current = null;
        } catch (err) {
          // Guard against "null" errors
          console.warn("Draw control cleanup failed:", err);
        }
      }
    };
  }, [mapRef.current]);

  useEffect(() => {
    const polygonCoords = newEventReqPolygon?.aoiPolygonWKT;
    console.log("MAP Polygon coordinates changed:", polygonCoords);
    setPolygonNotOK(false);

    if (!polygonCoords || polygonCoords.length === 0) {
      drawRef.current?.deleteAll();
      return;
    }
    const polygonCoordsArray = polygonCoords.replace(/^POLYGON\s*\(\(/i, "").replace(/\)\)$/, "");

    try {
      const cornersPairs = polygonCoordsArray.split(",");
      console.log("cornersPairs:", cornersPairs);

      const coords  : [number, number][]=cornersPairs.map((pair) => {
        const cornerCoords = pair.split(" ");

        const coord1 = parseFloat(cornerCoords[0]);
        const coord2 = parseFloat(cornerCoords[1]);
        if (isNaN(coord1) || isNaN(coord2)) {
          notify("Invalid AOI! The AOI shall be a POLYGON specified in Well-Known Text (WKT) format", {
            type: "warning",
            autoHideDuration: 5000,
          });
          throw new Error("Invalid coordinates in AOI polygon");
        }
        return([coord1, coord2]);
      });

      const first = coords[0];
      const last = coords[coords.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        coords.push(first); // Close the polygon

        const completedPolygonText = getPolygonInputText(coords);
        setNewEventReqPolygon((prev: Partial<EventScopeRequest> | null) => ({
          ...prev,
          aoiPolygonWKT: completedPolygonText,
        }));
      }

      const polygonFeature: Feature<Polygon> = {
        id: `polygon-${Date.now()}`,
        type: "Feature",
        properties: {},
        geometry: {
          type: "Polygon",
          coordinates: [coords],
        },
      };

      drawRef.current?.deleteAll();
      drawRef.current?.add(polygonFeature as any);
      console.log("Polygon feature added:", polygonFeature);

      // Zoom to fit the polygon
      const map = mapRef.current?.getMap();
      const bounds = coords.reduce(
        (b, [lng, lat]) => b.extend([lng, lat]),
        new mapboxgl.LngLatBounds(coords[0], coords[0]),
      );
      map?.fitBounds(bounds, { padding: 100 });

      const kinksPoints = kinks(polygonFeature);
      if (kinksPoints.features.length > 0) {
        notify(
          "The polygon has kinks (self intersections), which may cause issues with the area calculation.",
          { type: "warning", autoHideDuration: 5000 },
        );
        setPolygonNotOK(true);
        throw new Error("Polygon has kinks (self intersections)");
      }

      const insertedPolygonAreaInKM2 = getPolygonAreaInKM2(polygonFeature);
      if (insertedPolygonAreaInKM2 > maxPolygonAreaAccepted) {
        notify(exceedingPolygonAreaMessage(insertedPolygonAreaInKM2), {
          type: "warning",
          autoHideDuration: 5000,
        });
        setPolygonNotOK(true);
        throw new Error("Polygon area exceeds the maximum allowed size");
      }

      calcPolygonCountry(polygonFeature);
      calcPolygonBbox(polygonFeature);
    } catch (err) {
      console.log("Failed to parse coordinates", err);
    }
  }, [newEventReqPolygon?.aoiPolygonWKT]);

  useEffect(() => {
    setReqBtnDisabledFromMap(polygonNotOK);
  }, [polygonNotOK, setReqBtnDisabledFromMap]);

  const startRectangleDraw = () => {
    drawRef.current?.changeMode("draw_rectangle");
  };

  const onMove = (viewState: PartialViewState) => {
    setViewState(viewState);
  };

  const exceedingPolygonAreaMessage = (currAreaInKm2: number) =>
    `The selected area (${currAreaInKm2} km2) exceeds the maximum allowed size of ${maxPolygonAreaAccepted} km². `;
  const removeExtraDecimals = (coordinates: [number, number][]) => {
    return coordinates.map(([lng, lat]) => [
      Math.trunc(lng * 10000) / 10000,
      Math.trunc(lat * 10000) / 10000,
    ]);
  };
  const getPolygonInputText = (coordinates: [number, number][]) => {
    const noDecimalsCoords = removeExtraDecimals(coordinates);
    const polygonWKTFormat = noDecimalsCoords.map(([lng, lat]) => `${lng} ${lat}`).join(",");
    return `POLYGON ((${polygonWKTFormat}))`;
  };
  const getPolygonAreaInKM2 = (polygon: any) => {
    const newPolygonAreaValue = area(polygon); // in square meters
    const areaInKm2 = newPolygonAreaValue / 1_000_000; // convert to square kilometers

    return Math.trunc(areaInKm2 * 1000) / 1000;
  };

  const calcPolygonCountry = async (polygonFeature: Feature<Polygon>) => {
    if (!polygonFeature || !polygonFeature.geometry || !polygonFeature.geometry.coordinates) {
      setPolygonCountry(null);
      return;
    }

    const centerPoint = center(polygonFeature); // Returns a GeoJSON Point Feature
    if (!centerPoint || !centerPoint.geometry || !centerPoint.geometry.coordinates) {
      setPolygonCountry(null);
      return;
    }
    const [lng, lat] = centerPoint.geometry.coordinates;
    const mapBoxResponse = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?types=country&access_token=${MAPBOX_TOKEN}`,
    );

    const data = await mapBoxResponse.json();
    if (data.features.length === 0) {
      setPolygonCountry("WRL"); // Default to "World" if no country found
      return;
    }
    const countryAlpha2 = data.features[0]?.properties?.short_code?.toUpperCase();
    setPolygonCountry(getCountryCodeAlpha3(countryAlpha2));
  };

  const calcPolygonBbox = (polygonFeature: Feature<Polygon>) => {
    if (!polygonFeature || !polygonFeature.geometry || !polygonFeature.geometry.coordinates) {
      return "";
    }
    const bounds = bbox(polygonFeature); // returns [minX, minY, maxX, maxY]
    const bboxPoly = bboxPolygon(bounds); // create bbox polygon

    if (!bboxPoly) return "";
    const coords = bboxPoly?.geometry?.coordinates[0];
    const coordString = coords?.map(([lng, lat]) => `${lng} ${lat}`).join(",");
    const polygonParams: Partial<EventScopeRequest> = {
      aoiPolygonBBWKT: coordString ? `POLYGON ((${coordString}))` : "",
      aoiBB: `[${bounds.join(",")}]`,
    };

    setNewEventReqPolygon((prev: Partial<EventScopeRequest>) => ({
      ...prev,
      ...polygonParams,
    }));
  };

  return (
    <>
      <MapGL
        ref={mapRef}
        {...viewState}
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: "100%", height: "720px" }}
        mapStyle={mapStyle}
        onMove={(evt) => onMove(evt.viewState)}
      >
        <Box display="flex" justifyContent="space-between">
          <Box width={"50%"}>
            <MapSearchInputNoCountryCheck
              mapRef={mapRef}
              onViewStateChange={onMove}
            ></MapSearchInputNoCountryCheck>
          </Box>

          <Button
            onClick={startRectangleDraw}
            className="mapbox-gl-draw_ctrl-draw-btn"
            variant="contained"
            disableElevation={true}
            sx={{
              minWidth: "auto",
              padding: 1,
              marginRight: 5,
              marginTop: 1.2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "white",
              border: "2px solid #ddd",
              "&:hover": {
                backgroundColor: "#f2f2f2",
              },
              "&:click": {
                backgroundColor: "white",
              },
            }}
          >
            <Box
              sx={{
                width: 16,
                height: 10,
                backgroundColor: "white",
                border: "1px solid black",
                borderRadius: "2px",
              }}
            />
          </Button>
        </Box>

        <MapStylesMenu setMapStyle={setMapStyle}></MapStylesMenu>
      </MapGL>
    </>
  );
};
